'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import UnifiedProduct from '@/components/UnifiedProduct';
import AddToCart from '@/components/AddToCart';
import { getProduct } from '@/lib/api';
import { COLORS } from '@/constants/colors';
import { formatMakeName, formatMeasurement } from '@/utils/formatters';
import { getProductKind } from '@/utils/productKind';
import type { ShopifyOffer } from '@/lib/shopify';


interface Product {
  id?: number;
  title?: string;
  gfu_part_num?: string;
  price?: string | number;
  description?: string;
  buy_url?: string;
  product_type_name?: string;
  filter_color?: string;
  filter_shape?: string;
  filter_style?: string;
  filter_height_inch?: string;
  filter_id_width_inch?: string;
  filter_id_length_inch?: string;
  filter_od_length_inch?: string;
  filter_od_base_width_inch?: string;
  filter_od_base_length_inch?: string;
  filter_od_top_width_inch?: string;
  filter_od_top_length_inch?: string;
  filter_inlet_type?: string;
  filter_inlet_angle_deg?: string;
  filter_base?: string;
  filter_end_cap?: string;
  images?: Array<{
    link?: string;
  }>;
  models?: Array<{
    id?: number;
    title?: string;
    make?: string | { name?: string; title?: string; [key: string]: any };
    start_year?: string;
    end_year?: string;
    displacement?: string;
    engine?: string;
    intake?: string;
    description?: string;
  }>;
  [key: string]: any;
}

// True when both values are the same measurement, e.g. '2.00"' and '2.00'.
function sameMeasurement(a?: string, b?: string): boolean {
  const num = (v?: string) => parseFloat(String(v ?? '').replace(/[^0-9.]/g, ''));
  return !Number.isNaN(num(a)) && num(a) === num(b);
}

const INCH = ' in';

interface ProductPageProps {
  product: Product;
  offer?: ShopifyOffer | null;
}

const ProductPage: React.FC<ProductPageProps> = ({ product, offer }) => {
  const [cleanerKit, setCleanerKit] = useState<Product | null>(null);
  // The cleaner kit upsell only makes sense for washable filters, not for
  // accessories like wheel chocks or the cleaner kit itself.
  const showCleanerKit = product ? getProductKind(product) === 'filter' : false;

  // Fetch Cleaner Kit #2000 product data
  useEffect(() => {
    if (!showCleanerKit) return;
    async function fetchCleanerKit() {
      try { 
        const kitProduct = await getProduct({ gfu_part_num: '2000' });
        setCleanerKit(kitProduct);
      } catch (error) {
        console.error('Failed to fetch cleaner kit:', error);
      }
    }
    fetchCleanerKit();
  }, [showCleanerKit]);

  if (!product) {
    return (
      <>
        <Header />
        <div className="header-gap-creator"></div>
        <main>
        <NoProductSection>
          <div className="container-0">
            <h1>Product Not Found</h1>
            <p>The product you're looking for doesn't exist or has been removed.</p>
          </div>
        </NoProductSection>
        </main>
        <Footer />
      </>
    );
  }


  // Some legacy rows hold values in the wrong column: on panels, "Inside Length" and
  // "End Cap" contain the length/width in mm, and on round/cone filters "Inside Length"
  // just repeats the inside diameter. Only show those fields when they are real.
  const isPanel = product.filter_style === 'Panel';
  const insideDiameter = product.filter_id_width_inch || (isPanel ? undefined : product.filter_id_length_inch);
  const insideLength =
    !isPanel && !sameMeasurement(product.filter_id_length_inch, insideDiameter) ? product.filter_id_length_inch : undefined;
  const endCap = /^\s*[\d.]+\s*$/.test(product.filter_end_cap ?? '') ? undefined : product.filter_end_cap;
  const odBase = product.filter_od_base_width_inch;
  const odTop = product.filter_od_top_width_inch || product.filter_od_top_length_inch;

  // Build product specifications array - only show data from API, no hardcoded values
  const specifications = [
    { label: 'Filter Color', value: product.filter_color },
    { label: 'Filter Shape', value: product.filter_shape },
    { label: 'Filter Style', value: product.filter_style },
    { label: 'Height', value: formatMeasurement(product.filter_height_inch, INCH) },
    { label: 'Inside Diameter', value: formatMeasurement(insideDiameter, INCH) },
    { label: 'Inside Length', value: formatMeasurement(insideLength, INCH) },
    // Round filters have a single outside diameter instead of a base/top pair.
    { label: 'Outside Diameter', value: odBase || odTop ? undefined : formatMeasurement(product.filter_od_length_inch, INCH) },
    { label: 'Outside Diameter - Base', value: formatMeasurement(odBase, INCH) },
    { label: 'Outside Diameter - Top', value: formatMeasurement(odTop, INCH) },
    { label: 'Inlet Type', value: product.filter_inlet_type },
    { label: 'Inlet Angle', value: formatMeasurement(product.filter_inlet_angle_deg, '°') },
    { label: 'Base Material', value: product.filter_base },
    { label: 'End Cap', value: endCap },
  ].filter(spec => spec.value && spec.value.toString().trim());

  return (
    <>
      <Header />
      <div className="header-gap-creator"></div>
      <main className="container-0">
        {/* Hero Product Section */}
        <ProductHeroSection>
          <ProductHeroContainer>
            <ProductTitle>{product.title || 'Green Filter'}</ProductTitle>

            {/* Server-rendered offer summary, so the price and availability are in
                the page HTML and not only inside the Shopify widget's iframe. */}
            {offer && (
              <OfferSummary>
                <span className="price">${offer.price}</span>
                {offer.compareAtPrice && Number(offer.compareAtPrice) > Number(offer.price) && (
                  <s className="compare">${offer.compareAtPrice}</s>
                )}
                <span className="availability">{offer.availableForSale ? 'In stock' : 'Out of stock'}</span>
              </OfferSummary>
            )}

            {product.buy_url && product.id ? (
              <UnifiedProduct
                buyButtonId={product.buy_url}
                id={`product-${product.id}`}
              />
            ) : (
              <NoImagePlaceholder>
                <p>Product not available</p>
              </NoImagePlaceholder>
            )}

            <ProductFullWidthColumn>
              {/* Product Information Table */}
              <InfoSection>
                <InfoTitle>Product Information</InfoTitle>
                <InfoTableWrapper>
                  <InfoTable>
                    <thead>
                      <tr>
                        <th>Attribute</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {specifications.map((spec, idx) => (
                        <tr key={idx}>
                          <td>{spec.label}</td>
                          <td>{spec.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </InfoTable>
                </InfoTableWrapper>
              </InfoSection>

              {/* Recommended Add-on - Mobile Position (after product info) */}
              {showCleanerKit && (
              <MobileAddonSection>
                <MobileAddonTitle>Recommended Add-on</MobileAddonTitle>
                <AddonCard>
                  <AddonImageContainer>
                    <Image src="/assets/cleaner-p-800.webp" alt="Cleaner Kit #2000" width={800} height={800} />
                  </AddonImageContainer>
                  <AddonContent>
                    <AddonTitle>Cleaner Kit #2000</AddonTitle>
                    <AddonDescription>
                      Keep your Green Filter performing at its best. This professional cleaning kit includes everything you need for proper maintenance.
                    </AddonDescription>
                    {cleanerKit?.buy_url ? (
                      <AddonCartWrapper>
                        <AddToCart
                          buyButtonId={cleanerKit.buy_url}
                          id={`cleaner-kit-mobile-${cleanerKit.id}`}
                          background={COLORS.brandGreen}
                          color="#fff"
                          hidePrice={false}
                          isLeft={false}
                        />
                      </AddonCartWrapper>
                    ) : (
                      <ViewProductButton onClick={() => window.location.href = '/store/filter/2000'}>
                        View Product
                      </ViewProductButton>
                    )}
                  </AddonContent>
                </AddonCard>
              </MobileAddonSection>
              )}
            </ProductFullWidthColumn>
          </ProductHeroContainer>
        </ProductHeroSection>

        {/* Recommended Add-on - Desktop Position */}
        {showCleanerKit && (
        <DesktopAddonSection>
          <AddonContainer>
            <AddonSectionTitle>Recommended Add-on</AddonSectionTitle>
            <AddonCardLarge>
              <AddonImageLargeContainer>
                <Image src="/assets/cleaner-p-800.webp" alt="Cleaner Kit #2000" width={800} height={800} />
              </AddonImageLargeContainer>
              <AddonInfoLarge>
                <AddonTitleLarge>Cleaner Kit #2000</AddonTitleLarge>
                <AddonDescriptionLarge>
                  Keep your Green Filter performing at its best. This professional cleaning kit includes everything you need for proper maintenance and optimal airflow performance.
                </AddonDescriptionLarge>
                {cleanerKit?.buy_url ? (
                  <AddonLargeCartWrapper>
                    <AddToCart
                      buyButtonId={cleanerKit.buy_url}
                      id={`cleaner-kit-desktop-${cleanerKit.id}`}
                      background={COLORS.brandGreen}
                      color="#fff"
                      hidePrice={false}
                      isLeft={false}
                    />
                  </AddonLargeCartWrapper>
                ) : (
                  <ViewProductButtonLarge onClick={() => window.location.href = '/store/filter/2000'}>
                    View Product
                  </ViewProductButtonLarge>
                )}
              </AddonInfoLarge>
            </AddonCardLarge>
          </AddonContainer>
        </DesktopAddonSection>
        )}

        {/* Vehicle Fitment Section */}
        {product.models && product.models.length > 0 && (
          <FitmentSection>
            <FitmentContainer>
              <FitmentTitle>Vehicle Fitment</FitmentTitle>

              <TableWrapper>
                <VehicleTable>
                  <thead>
                    <tr>
                      <th>Years</th>
                      <th>Make</th>
                      <th>Model</th>
                      <th>Disp.</th>
                      <th>Engine</th>
                      <th>Intake</th>
                      <th>Fits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.models.map((model, idx) => {
                      const makeName = formatMakeName(model.make);

                      return (
                        <tr key={`model-${idx}-${model.id || ''}-${makeName}-${model.title}`}>
                          <td>{model.start_year}-{model.end_year}</td>
                          <td>{makeName}</td>
                          <td>{model.title}</td>
                          <td>{model.displacement}</td>
                          <td>{model.engine}</td>
                          <td>{model.intake}</td>
                          <td>{model.description || 'All'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </VehicleTable>
              </TableWrapper>
            </FitmentContainer>
          </FitmentSection>
        )}

      </main>
      <Footer />
    </>
  );
};

export default ProductPage;

// Styled Components Following Site Design Language
const NoProductSection = styled.section`
  padding: 80px 24px;
  text-align: center;
  background: #fafafa;

  h1 {
    font-family: Lato, sans-serif;
    font-size: 36px;
    font-weight: 700;
    color: #313131;
    margin-bottom: 16px;
  }

  p {
    font-family: Lato, sans-serif;
    font-size: 18px;
    color: #757575;
  }

  @media (max-width: 767px) {
    padding: 48px 16px;

    h1 {
      font-size: 28px;
    }
  }
`;

const ProductHeroSection = styled.section`
  background: #fff;
  padding: 40px 24px 32px;

  @media (max-width: 1024px) {
    padding: 32px 24px 24px;
  }

  @media (max-width: 767px) {
    padding: 24px 16px 20px;
  }
`;

const ProductHeroContainer = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const ProductFullWidthColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const NoImagePlaceholder = styled.div`
  width: 100%;
  min-height: 400px;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%);
  border: 2px dashed #e0e0e0;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #999;
  font-family: Lato, sans-serif;
  padding: 40px 20px;

  &::before {
    content: '🖼️';
    font-size: 64px;
    opacity: 0.3;
  }

  p {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    color: #999;
  }

  @media (max-width: 767px) {
    min-height: 300px;
    padding: 32px 16px;

    &::before {
      font-size: 48px;
    }

    p {
      font-size: 14px;
    }
  }
`;

const MobileAddonSection = styled.div`
  display: none;
  margin-top: 24px;

  @media (max-width: 991px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

const MobileAddonTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #313131;
  margin: 0 0 12px 0;

  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

const DesktopAddonSection = styled.section`
  padding: 0 24px 32px;

  @media (max-width: 991px) {
    display: none;
  }

  @media (max-width: 767px) {
    padding: 0 16px 24px;
  }
`;

const AddonContainer = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const AddonSectionTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #313131;
  text-align: center;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    font-size: 26px;
    margin-bottom: 24px;
  }
`;

const AddonCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${COLORS.brandGreen};
    box-shadow: 0 4px 12px rgba(0, 173, 35, 0.1);
  }

  @media (max-width: 767px) {
    padding: 12px;
    gap: 12px;
  }
`;

const AddonCardLarge = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 40px;
  padding: 40px;
  background: #fff;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${COLORS.brandGreen};
    box-shadow: 0 6px 20px rgba(0, 173, 35, 0.12);
  }

  @media (max-width: 991px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 24px;
    padding: 32px 24px;
  }
`;

const AddonImageContainer = styled.div`
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border-radius: 6px;
  padding: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 767px) {
    width: 80px;
    height: 80px;
    padding: 8px;
  }
`;

const AddonImageLargeContainer = styled.div`
  flex-shrink: 0;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border-radius: 12px;
  padding: 20px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 991px) {
    width: 160px;
    height: 160px;
  }
`;

const AddonContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 767px) {
    gap: 6px;
  }
`;

const AddonInfoLarge = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AddonTitle = styled.div`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #313131;
  margin-bottom: 4px;

  @media (max-width: 767px) {
    font-size: 15px;
  }
`;

const AddonDescription = styled.p`
  font-family: Lato, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  color: #757575;
  margin: 0 0 8px 0;

  @media (max-width: 767px) {
    font-size: 12px;
    line-height: 1.3;
    margin: 0 0 6px 0;
  }
`;

const AddonTitleLarge = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #313131;
  margin: 0 0 12px 0;

  @media (max-width: 991px) {
    font-size: 22px;
  }
`;

const AddonDescriptionLarge = styled.p`
  font-family: Lato, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #757575;
  margin: 0 0 24px 0;

  @media (max-width: 991px) {
    font-size: 14px;
  }
`;

const ViewProductButton = styled.button`
  padding: 10px 20px;
  background: ${COLORS.brandGreen};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: Lato, sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 173, 35, 0.2);
  width: 100%;
  margin-top: 16px;

  &:hover {
    background: #009c1f;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 173, 35, 0.3);
  }

  &:active {
    background: #008a1c;
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 173, 35, 0.2);
  }
`;

const ViewProductButtonLarge = styled.button`
  padding: 12px 28px;
  background: ${COLORS.brandGreen};
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 173, 35, 0.2);
  margin-top: 24px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #009c1f;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 173, 35, 0.3);
  }

  &:active {
    background: #008a1c;
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 173, 35, 0.2);
  }

  @media (max-width: 991px) {
    font-size: 13px;
    padding: 10px 24px;
  }
`;

const AddonCartWrapper = styled.div`
  margin-top: 8px;

  /* Make addon buttons smaller for mobile */
  .shopify-buy__product__price {
    font-size: 20px !important;
    margin-bottom: 8px !important;
  }

  button.shopify-buy__btn {
    font-size: 12px !important;
    padding: 8px 16px !important;
    min-height: 36px !important;
  }

  @media (max-width: 767px) {
    margin-top: 6px;

    .shopify-buy__product__price {
      font-size: 18px !important;
      margin-bottom: 6px !important;
    }

    button.shopify-buy__btn {
      font-size: 11px !important;
      padding: 6px 14px !important;
      min-height: 32px !important;
    }
  }
`;

const AddonLargeCartWrapper = styled.div`
  margin-top: 24px;

  /* Make addon buttons smaller for desktop */
  .shopify-buy__product__price {
    font-size: 28px !important;
    margin-bottom: 16px !important;
  }

  button.shopify-buy__btn {
    font-size: 14px !important;
    padding: 12px 28px !important;
    min-height: 44px !important;
  }
`;


const ProductTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: #313131;
  line-height: 1.2;
  margin: 0 0 32px 0;
  text-align: center;

  @media (max-width: 991px) {
    font-size: 30px;
    margin: 0 0 24px 0;
  }

  @media (max-width: 767px) {
    font-size: 26px;
    margin: 0 0 20px 0;
  }
`;

const OfferSummary = styled.p`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  gap: 12px;
  margin: -16px 0 24px;
  font-family: Lato, sans-serif;

  .price {
    font-size: 22px;
    font-weight: 700;
    color: ${COLORS.textPrimary};
  }

  .compare {
    font-size: 16px;
    color: ${COLORS.textMuted};
  }

  .availability {
    font-size: 14px;
    color: ${COLORS.brandGreenAccessible};
  }
`;

const InfoSection = styled.div`
  margin-top: 0;
`;

const InfoTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #313131;
  margin-bottom: 16px;
`;

const InfoTableWrapper = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
`;

const InfoTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: ${COLORS.brandGreen};

    th {
      padding: 12px 16px;
      text-align: left;
      font-family: Lato, sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #fff;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background: #fafafa;
      }

      &:hover {
        background: #f0f9f2;
      }
    }

    td {
      padding: 12px 16px;
      font-family: Lato, sans-serif;
      font-size: 14px;
      color: #525252;
      border-bottom: 1px solid #e8e8e8;

      &:first-child {
        font-weight: 600;
        color: #757575;
      }
    }

    tr:last-child td {
      border-bottom: none;
    }
  }
`;

const FitmentSection = styled.section`
  padding: 40px 24px;
  background: #111;

  @media (max-width: 767px) {
    padding: 32px 16px;
  }
`;

const FitmentContainer = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const FitmentTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  margin-bottom: 32px;

  @media (max-width: 767px) {
    font-size: 26px;
    margin-bottom: 24px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const VehicleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;

  thead {
    background: ${COLORS.brandGreen};
    position: sticky;
    top: 0;
    z-index: 10;

    th {
      padding: 14px 12px;
      text-align: left;
      font-family: Lato, sans-serif;
      font-size: 12px;
      font-weight: 600;
      color: #fff;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background: #fafafa;
      }

      &:hover {
        background: #f0f9f2;
      }
    }

    td {
      padding: 12px;
      font-family: Lato, sans-serif;
      font-size: 13px;
      color: #525252;
      border-bottom: 1px solid #e8e8e8;
      white-space: nowrap;
    }

    tr:last-child td {
      border-bottom: none;
    }
  }
`;
